import React, { useState, useEffect } from 'react';
import { FiEdit2, FiCalendar, FiFlag, FiFileText, FiTag, FiList, FiPlus } from 'react-icons/fi';
import './EditTaskModal.css';

const EditTaskModal = ({ task, subtasks, subtasksLoading, onClose, onSave, onSubtaskStatusChange, fetchSubtasks, onAddSubtask, API_URL }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        dueTime: '',
        tags: ''
    });

    useEffect(() => {
        if (task) {
            const dueDateTime = task.dueDate ? new Date(task.dueDate) : null;
            setFormData({
                title: task.title || '',
                description: task.description || '',
                priority: task.priority || 'medium',
                dueDate: dueDateTime ? dueDateTime.toISOString().slice(0, 10) : '',
                dueTime: dueDateTime ? dueDateTime.toISOString().slice(11, 16) : '',
                tags: task.tags ? task.tags.join(', ') : ''
            });
        }
    }, [task]);

    // Fetch subtasks when modal opens
    useEffect(() => {
        if (task && task._id && fetchSubtasks) {
            fetchSubtasks(task._id);
        }
    }, [task, fetchSubtasks]);

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            alert('Please enter a task title');
            return;
        }

        if (!formData.description.trim()) {
            alert('Please enter a task description');
            return;
        }

        if (!formData.dueDate) {
            alert('Please select a due date');
            return;
        }

        // Combine date and time
        const timeToUse = formData.dueTime || '23:59';
        const dueDateTimeString = `${formData.dueDate}T${timeToUse}:00`;

        const updatedData = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            priority: formData.priority,
            dueDate: dueDateTimeString,
            tags: formData.tags.trim()
        };

        onSave(updatedData);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (!task) return null;

    return (
        <div className="edit-task-modal-overlay" onClick={onClose}>
            <div className="edit-task-modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="edit-task-modal-header">
                    <div className="edit-task-modal-title-section">
                        <div className="edit-task-header-icon">
                            <FiEdit2 size={24} />
                        </div>
                        <h2>Edit Task</h2>
                        <button 
                            className="edit-task-modal-close-btn" 
                            onClick={onClose}
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>
                    <div className="edit-task-id">
                        Task ID: <span className="task-id-badge">{task.taskId || 'N/A'}</span>
                    </div>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit}>
                    <div className="edit-task-modal-body">
                        {/* Title */}
                        <div className="form-section">
                            <div className="form-section-header">
                                <FiFileText size={18} />
                                <span>Task Details</span>
                            </div>
                            <div className="form-group">
                                <label htmlFor="task-title">
                                    Title <span className="required">*</span>
                                </label>
                                <input
                                    id="task-title"
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    placeholder="Enter task title"
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="task-description">
                                    Description <span className="required">*</span>
                                </label>
                                <textarea
                                    id="task-description"
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    placeholder="Enter task description"
                                    className="form-control"
                                    rows={5}
                                    required
                                />
                            </div>
                        </div>

                        {/* Subtasks Section - Moved up for visibility */}
                        {!task.parentTask && (
                            <div className="form-section" style={{background: '#f0fdf4', padding: '16px', borderRadius: '8px'}}>
                                <div className="form-section-header">
                                    <FiList size={18} />
                                    <span>Subtasks ({subtasks?.length || 0})</span>
                                    {onAddSubtask && (
                                        <button 
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onAddSubtask();
                                            }}
                                            className="btn-add-subtask"
                                            title="Add Subtask"
                                        >
                                            <FiPlus size={16} />
                                            Add Subtask
                                        </button>
                                    )}
                                </div>
                                {subtasksLoading ? (
                                    <div className="subtasks-loading">Loading subtasks...</div>
                                ) : subtasks && subtasks.length > 0 ? (
                                    <div className="edit-subtasks-list">
                                        {subtasks.map((subtask) => (
                                            <div key={subtask._id} className="edit-subtask-item">
                                                <div className="edit-subtask-header">
                                                    <div className="edit-subtask-title-section">
                                                        <span className="edit-subtask-id">{subtask.taskId}</span>
                                                        <span className="edit-subtask-title">{subtask.title}</span>
                                                    </div>
                                                    <div className="edit-subtask-status-section">
                                                        <select 
                                                            value={subtask.status} 
                                                            onChange={(e) => onSubtaskStatusChange(subtask._id, e.target.value)}
                                                            className={`edit-subtask-status-select ${subtask.status}`}
                                                        >
                                                            <option value="todo">To Do</option>
                                                            <option value="in-progress">In Progress</option>
                                                            <option value="review">Review</option>
                                                            <option value="completed">Completed</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="edit-subtask-details">
                                                    <div className="edit-subtask-description">
                                                        {subtask.description}
                                                    </div>
                                                    <div className="edit-subtask-meta">
                                                        <span className={`edit-subtask-priority ${subtask.priority}`}>
                                                            {subtask.priority}
                                                        </span>
                                                        <span className="edit-subtask-assignee">
                                                            {Array.isArray(subtask.assignedToName) 
                                                                ? subtask.assignedToName.join(', ') 
                                                                : subtask.assignedToName}
                                                        </span>
                                                        <span className="edit-subtask-due-date">
                                                            Due: {new Date(subtask.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="no-subtasks">
                                        No subtasks yet. Click "Add Subtask" to create one.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Priority & Due Date */}
                        <div className="form-section">
                            <div className="form-section-header">
                                <FiFlag size={18} />
                                <span>Priority & Schedule</span>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="task-priority">
                                        Priority <span className="required">*</span>
                                    </label>
                                    <select
                                        id="task-priority"
                                        value={formData.priority}
                                        onChange={(e) => handleChange('priority', e.target.value)}
                                        className="form-control"
                                        required
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                    <div className="priority-indicator">
                                        <span className={`priority-dot ${formData.priority}`}></span>
                                        <span className="priority-label">{formData.priority}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="task-due-date">
                                        Due Date <span className="required">*</span>
                                    </label>
                                    <div className="input-with-icon">
                                        <FiCalendar className="input-icon" size={18} />
                                        <input
                                            id="task-due-date"
                                            type="date"
                                            value={formData.dueDate}
                                            className="form-control with-icon"
                                            disabled
                                            readOnly
                                            style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="task-due-time">
                                        Due Time
                                    </label>
                                    <input
                                        id="task-due-time"
                                        type="time"
                                        value={formData.dueTime}
                                        className="form-control"
                                        disabled
                                        readOnly
                                        style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Tags */}
                        <div className="form-section">
                            <div className="form-section-header">
                                <FiTag size={18} />
                                <span>Tags (Optional)</span>
                            </div>
                            <div className="form-group">
                                <label htmlFor="task-tags">
                                    Tags
                                </label>
                                <input
                                    id="task-tags"
                                    type="text"
                                    value={formData.tags}
                                    onChange={(e) => handleChange('tags', e.target.value)}
                                    placeholder="e.g. urgent, client-request, bug-fix (comma separated)"
                                    className="form-control"
                                />
                                <div className="form-hint">
                                    Separate multiple tags with commas
                                </div>
                            </div>
                        </div>

                        {/* Current Assignment Info */}
                        <div className="form-section info-section">
                            <div className="info-item">
                                <span className="info-label">Assigned To:</span>
                                <span className="info-value">
                                    {Array.isArray(task.assignedToName) 
                                        ? task.assignedToName.join(', ') 
                                        : task.assignedToName}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Assigned By:</span>
                                <span className="info-value">{task.assignedByName}</span>
                            </div>
                            <div className="info-hint">
                                To change assignees, use the "Reassign" option
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="edit-task-modal-footer">
                        <button type="button" onClick={onClose} className="btn-secondary">
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary">
                            <FiEdit2 size={18} />
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditTaskModal;
